/**
 * BrainDumpSearch — hybrid search UI with 300ms debounce.
 *
 * A13-N12. Sub-view within BrainDumpPage (tab navigation).
 *
 * Props:
 *  - onNoteSelect: (noteId: string) => void
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { AnimatedList } from '../motion';

const API_PREFIX = '/api/v1/aif/brain-dump';
const CAPTURE_ICONS = { text: '\u270D\uFE0F', voice: '\uD83C\uDFA4', photo: '\uD83D\uDCF7', mixed: '\uD83D\uDCDD', chat: '\uD83D\uDCAC' };
const DEBOUNCE_MS = 300;

function HighlightedText({ text, query }) {
  if (!text || !query) return text || null;
  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-[var(--aif-color-warning-light)] rounded px-0.5">{part}</mark>
      ) : (
        part
      )
    );
  } catch {
    return text;
  }
}

export default function BrainDumpSearch({ onNoteSelect }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);

  async function performSearch(q) {
    if (!q || q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `${API_PREFIX}/search?q=${encodeURIComponent(q)}`
      );
      const data = res.data ?? res;
      setResults(data.results || []);
      setSearched(true);
    } catch (err) {
      setError(err.message || t('brainDump.searchError', 'Suche fehlgeschlagen'));
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="search-view">
      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('brainDump.searchPlaceholder', 'Notizen durchsuchen...')}
          className="w-full px-4 py-3 pl-10 border border-[var(--aif-border-default)] rounded-lg text-base bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] placeholder:text-[var(--aif-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--aif-color-primary)]"
          data-testid="search-input"
          autoFocus
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--aif-text-muted)]">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[var(--aif-text-muted)]" data-testid="search-loading">
            {t('brainDump.searching', 'sucht...')}
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-[var(--aif-color-danger-light)] border border-[var(--aif-color-danger-border)] text-[var(--aif-color-danger)] px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Empty state */}
      {searched && !loading && results.length === 0 && !error && (
        <div className="text-[var(--aif-text-muted)] text-sm text-center py-8" data-testid="search-empty">
          {t('brainDump.noResults', 'Keine Ergebnisse f\u00FCr')} &bdquo;{query}&ldquo;
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div>
          <p className="text-xs text-[var(--aif-text-muted)] mb-2" data-testid="results-count">
            {results.length} {t('brainDump.hits', 'Treffer')}
          </p>
          <AnimatedList className="space-y-2" data-testid="search-results">
            {results.map((r) => (
              <div
                key={r.note_id}
                onClick={() => onNoteSelect(r.note_id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onNoteSelect(r.note_id)}
                className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-lg p-3 cursor-pointer hover:shadow-sm transition"
              >
                <div className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)] mb-1">
                  <span>{CAPTURE_ICONS[r.capture_type] || '\uD83D\uDCDD'}</span>
                  <span>{new Date(r.captured_at).toLocaleDateString('de-DE')}</span>
                  {r.location_label && <span>· {r.location_label}</span>}
                  {r.document_type && r.document_type !== 'note' && (
                    <span className="ml-auto text-[var(--aif-color-warning)]">{r.document_type}</span>
                  )}
                </div>
                <p className="text-sm text-[var(--aif-text-primary)] line-clamp-2">
                  <HighlightedText text={r.structured_summary || r.raw_text} query={query} />
                </p>
                <div className="mt-1 text-xs text-[var(--aif-text-muted)]">
                  {t('brainDump.relevance', 'Relevanz')}: {Math.round((r.combined_rank || 0) * 100)}%
                </div>
              </div>
            ))}
          </AnimatedList>
        </div>
      )}
    </div>
  );
}
